import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMembers } from '../../../services/organizationService';
import type { DeptUnit } from '../../../services/executiveDeptUnitsService';
import { useTeamCapacityData } from './useTeamCapacityData';
import { buildAggregatesMap, ORG_MEMBERS_FULL_QUERY_KEY, type DeptUnitAggregate } from './deptUnitAggregates';

export function useDeptUnitAggregates(
  organizationId: string | undefined,
  units: DeptUnit[],
): {
  aggregatesByDeptId: Map<string, DeptUnitAggregate>;
  isLoading: boolean;
} {
  const { items, members: workloadMembers, isLoading: capacityLoading } = useTeamCapacityData(
    organizationId,
    { refetchInterval: 30_000 },
  );

  const orgMembersQuery = useQuery({
    queryKey: ORG_MEMBERS_FULL_QUERY_KEY(organizationId ?? ''),
    queryFn: () => getMembers(organizationId!),
    enabled: Boolean(organizationId),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

  const aggregatesByDeptId = useMemo(
    () => buildAggregatesMap(units, orgMembersQuery.data ?? [], workloadMembers, items),
    [units, orgMembersQuery.data, workloadMembers, items],
  );

  return {
    aggregatesByDeptId,
    isLoading: capacityLoading || orgMembersQuery.isLoading,
  };
}
